<script setup lang="ts">
import * as d3 from 'd3'
import AssistantMessageChartTooltip from '@/components/assistant/chat/message/AssistantMessageChartTooltip.vue'
import type {ChartData, ChartSeries} from '@/client/mentor/MessagesClient'
import {toRef} from 'vue'
import {useAssistantMessageChartLifecycle} from '@/components/assistant/composables/useAssistantMessageChartLifecycle'
import {useAssistantMessageChart} from '@/components/assistant/composables/useAssistantMessageChart'

interface Props {
  chartData: ChartData
}

const props = defineProps<Props>()
const chartData = toRef(props, 'chartData')

const {
  chartTheme,
  chartContainer,
  hiddenLegendItems,
  hoverTooltip,
  cartesianLegendItems,
  visibleSeries,
  allCategories,
  attachTooltipHandlers,
  hideTooltip,
  formatTooltipValue,
  formatXAxisLabel,
  toNumericValue,
  getXAxisTickValues,
  shouldRotateXAxisTickLabels,
  getXAxisBottomMargin,
  getLegendHeight,
  renderLegendInSvg,
} = useAssistantMessageChart(chartData)

useAssistantMessageChartLifecycle(chartContainer, chartData, hiddenLegendItems, drawChart)

function drawChart() {
  if (!chartContainer.value) {
    return
  }

  const container = chartContainer.value
  const width = container.clientWidth || 320
  const defaultMargin = {top: 32, right: 24, bottom: 40, left: 72}
  const categories = allCategories.value

  if (!categories.length) {
    d3.select(container).selectAll('*').remove()
    hideTooltip()
    return
  }

  const initialPlotWidth = width - defaultMargin.left - defaultMargin.right
  const xAxisTicks = getXAxisTickValues(categories, initialPlotWidth)
  const shouldRotateXAxisLabels = shouldRotateXAxisTickLabels(xAxisTicks.formattedLabels, initialPlotWidth)
  const margin = {
    ...defaultMargin,
    bottom: getXAxisBottomMargin(xAxisTicks.formattedLabels, shouldRotateXAxisLabels, defaultMargin.bottom),
  }

  const chartHeight = 220
  const legendHeight = getLegendHeight(cartesianLegendItems.value, width - margin.left - margin.right)
  const height = chartHeight + margin.top + margin.bottom + legendHeight

  d3.select(container).selectAll('*').remove()
  hideTooltip()

  const svg = d3
    .select(container)
    .append('svg')
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', `0 0 ${width} ${height}`)
    .attr('class', 'overflow-visible')

  const plotWidth = width - margin.left - margin.right
  const plotHeight = chartHeight
  const plot = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`)
  const colorScale = d3
    .scaleOrdinal<string>()
    .domain(cartesianLegendItems.value.map((item) => item.key))
    .range(cartesianLegendItems.value.map((item) => item.color))

  const numericValues = visibleSeries.value.flatMap((series) =>
    (series.points ?? []).flatMap((point) => {
      const value = toNumericValue(point.y)
      return value === null ? [] : [value]
    })
  )
  const maxValue = d3.max(numericValues) ?? 0

  const xScale = d3.scaleBand<string>().domain(categories).range([0, plotWidth]).padding(0.24)
  const yScale = d3
    .scaleLinear()
    .domain([0, maxValue || 1])
    .nice()
    .range([plotHeight, 0])

  plot
    .append('g')
    .attr('transform', `translate(0, ${plotHeight})`)
    .call(
      d3
        .axisBottom(xScale)
        .tickValues(xAxisTicks.values)
        .tickFormat((value) => formatXAxisLabel(String(value)))
    )
    .call((axis) => {
      const text = axis.selectAll('text').attr('fill', chartTheme.axisTextColor).attr('font-size', 11)

      if (shouldRotateXAxisLabels) {
        text.attr('text-anchor', 'end').attr('transform', 'rotate(-45)')
      }
    })
    .call((axis) => axis.selectAll('path,line').attr('stroke', chartTheme.axisStrokeColor))

  const yAxis = plot
    .append('g')
    .call(
      d3
        .axisLeft(yScale)
        .ticks(5)
        .tickFormat((value) => {
          const numericValue = typeof value === 'number' ? value : Number(value)
          return `${numericValue}`
        })
    )
    .call((axis) => axis.selectAll('text').attr('fill', chartTheme.axisTextColor).attr('font-size', 11))
    .call((axis) => axis.selectAll('path,line').attr('stroke', chartTheme.axisStrokeColor))

  plot
    .append('g')
    .selectAll('line')
    .data(yScale.ticks(5))
    .join('line')
    .attr('x1', 0)
    .attr('x2', plotWidth)
    .attr('y1', (tick) => yScale(tick))
    .attr('y2', (tick) => yScale(tick))
    .attr('stroke', chartTheme.gridStrokeColor)
    .attr('stroke-dasharray', '2 4')

  drawBars(plot, xScale, yScale, plotHeight, visibleSeries.value, colorScale)

  if (chartData.value.xLabel) {
    svg
      .append('text')
      .attr('x', margin.left + plotWidth / 2)
      .attr('y', height - legendHeight - (shouldRotateXAxisLabels ? 8 : 12))
      .attr('text-anchor', 'middle')
      .attr('fill', chartTheme.axisLabelColor)
      .attr('font-size', 11)
      .text(chartData.value.xLabel)
  }

  if (chartData.value.yLabel) {
    const yAxisNode = yAxis.node()
    const yAxisBounds = yAxisNode?.getBBox()
    const yAxisLabelX = yAxisBounds ? Math.max(8, margin.left + yAxisBounds.x - 16) : Math.max(8, margin.left - 56)

    svg
      .append('text')
      .attr('transform', `translate(${yAxisLabelX}, ${margin.top + plotHeight / 2}) rotate(-90)`)
      .attr('text-anchor', 'middle')
      .attr('fill', chartTheme.axisLabelColor)
      .attr('font-size', 11)
      .text(chartData.value.yLabel)
  }

  renderLegendInSvg(svg, cartesianLegendItems.value, margin, 'cartesian', shouldRotateXAxisLabels, legendHeight)
}

function drawBars(
  plot: d3.Selection<SVGGElement, unknown, null, undefined>,
  xScale: d3.ScaleBand<string>,
  yScale: d3.ScaleLinear<number, number>,
  plotHeight: number,
  visible: ChartSeries[],
  colorScale: d3.ScaleOrdinal<string, string, never>
) {
  if (visible.length > 1) {
    const innerScale = d3
      .scaleBand<string>()
      .domain(visible.map((series) => series.name))
      .range([0, xScale.bandwidth()])
      .padding(0.12)

    for (const series of visible) {
      const seriesData = (series.points ?? []).flatMap((point) => {
        const y = toNumericValue(point.y)
        return y === null ? [] : [{x: String(point.x), y}]
      })

      const bars = plot
        .append('g')
        .selectAll('rect')
        .data(seriesData)
        .join('rect')
        .attr('x', (point) => (xScale(point.x) ?? 0) + (innerScale(series.name) ?? 0))
        .attr('y', (point) => yScale(point.y))
        .attr('width', innerScale.bandwidth())
        .attr('height', (point) => plotHeight - yScale(point.y))
        .attr('rx', 4)
        .attr('fill', colorScale(series.name))

      attachTooltipHandlers(bars, (point) => ({
        title: `${series.name} - ${formatXAxisLabel(point.x)}`,
        value: formatTooltipValue(point.y, series.unit),
      }))
    }

    return
  }

  const series = visible[0]
  if (!series) {
    return
  }

  const seriesData = (series.points ?? []).flatMap((point) => {
    const y = toNumericValue(point.y)
    return y === null ? [] : [{x: String(point.x), y}]
  })
  const barColor = colorScale(series.name)

  const bars = plot
    .append('g')
    .selectAll('rect')
    .data(seriesData)
    .join('rect')
    .attr('x', (point) => xScale(point.x) ?? 0)
    .attr('y', (point) => yScale(point.y))
    .attr('width', xScale.bandwidth())
    .attr('height', (point) => plotHeight - yScale(point.y))
    .attr('rx', 4)
    .attr('fill', barColor)

  attachTooltipHandlers(bars, (point) => ({
    title: formatXAxisLabel(point.x),
    value: formatTooltipValue(point.y, series.unit),
  }))
}
</script>

<template>
  <div class="relative w-full rounded-xl border" :class="chartTheme.containerClass">
    <div ref="chartContainer" class="w-full overflow-hidden"></div>

    <AssistantMessageChartTooltip
      :container-class="chartTheme.tooltipContainerClass"
      :title-class="chartTheme.tooltipTitleClass"
      :tooltip="hoverTooltip"
      :value-class="chartTheme.tooltipValueClass"
    />
  </div>
</template>
